// components/Admin/PresenterView.tsx
//
// The BROADCAST screen — this is what the host captures into their YouTube live
// stream (OBS / screen-share). It is read-only: all pacing happens in
// SessionManager. Optimised for landscape/16:9 and high-contrast stream capture.
//
// Because the phone is a shape-only answer pad in stream mode, this screen is the
// ONLY place players can read the question + which shape maps to which option —
// so options are rendered WITH their shape + colour to match the phone tiles.
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Quiz, SessionState, Participant, LeaderboardEntry } from '../../types/quiz.types';
import {
  getQuiz,
  subscribeToSession,
  subscribeToLeaderboard,
  subscribeToAnswerCounts,
  subscribeToParticipants,
  serverNow,
} from '../../services/live-session';
import QRCode from 'qrcode';
import { toEmbedUrl } from '../../utils/youtube';
import { tileClass, shapeOf, TILE_FRAME, REVEAL_CORRECT, REVEAL_DIMMED, rankChip } from '../../constants/answer-tiles';

// Module scope — an inline definition would remount (and re-flash) the whole
// screen on every state change.
const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-ink text-white flex flex-col relative">
    {children}
    {/* On-stream maker's mark — every second of broadcast carries the brand.
        Kept faint and inside the TV-safe margin so it never fights the stage. */}
    <p className="absolute bottom-4 right-6 text-sm font-semibold text-white/25 select-none pointer-events-none">
      QuizLive · by aliensbrain
    </p>
  </div>
);

// QR straight to /?pin=XXXXXX — scanned off the stream, it lands the viewer on
// the join screen with the PIN pre-filled (and the PIN survives the sign-in
// redirect). Uses window.location.origin, so a custom domain Just Works.
const JoinQr = ({ url, size }: { url: string; size: number }) => {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(url, {
      width: size * 2, // 2x for crisp capture after stream compression
      margin: 1,
      color: { dark: '#16092E', light: '#FFFFFF' },
    })
      .then((d) => { if (alive) setSrc(d); })
      .catch(() => { /* QR is a bonus — the PIN on screen still works */ });
    return () => { alive = false; };
  }, [url, size]);
  if (!src) return null;
  return <img src={src} alt={`Scan to join: ${url}`} width={size} height={size} className="rounded-lg" />;
};

// How long a shout-out owns the broadcast. Timed on the server clock (the
// shoutout carries a serverTimestamp), so a refreshed presenter tab doesn't
// replay an old one.
const SHOUTOUT_MS = 8000;

// Full-screen shout-out moment — the free lottery for the bottom 90%: any
// player can land on the broadcast, not just the top 3.
const ShoutoutOverlay = ({ shoutout }: { shoutout: { name: string; avatar?: string } }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-ink/95">
    <div className="text-center animate-pop px-12">
      <p className="uppercase tracking-[0.3em] text-accent font-semibold text-2xl">📣 Shout-out</p>
      <p className="text-8xl font-display font-extrabold tracking-tight mt-6 break-words">
        {shoutout.avatar ? `${shoutout.avatar} ` : ''}{shoutout.name}
      </p>
      <p className="text-white/60 text-2xl mt-6">is playing along right now!</p>
    </div>
  </div>
);

export const PresenterView = () => {
  const { sessionCode } = useParams<{ sessionCode: string }>();
  const [session, setSession] = useState<SessionState | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [ranks, setRanks] = useState<LeaderboardEntry[]>([]);
  const [answersByUser, setAnswersByUser] = useState<Record<string, Record<string, number>>>({});
  const [participants, setParticipants] = useState<Array<{ uid: string } & Participant>>([]);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [shoutoutVisible, setShoutoutVisible] = useState(false);
  const fetchedQuizId = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionCode) return;
    const unsubSession = subscribeToSession(sessionCode, setSession);
    const unsubRanks = subscribeToLeaderboard(sessionCode, setRanks);
    // Admin-only listeners (this is the host's client) — answered count and the
    // lobby roster are great social proof on stream and never fan out to players.
    const unsubAnswers = subscribeToAnswerCounts(sessionCode, setAnswersByUser);
    const unsubParticipants = subscribeToParticipants(sessionCode, setParticipants);
    return () => { unsubSession(); unsubRanks(); unsubAnswers(); unsubParticipants(); };
  }, [sessionCode]);

  useEffect(() => {
    const quizId = session?.quizId;
    if (!quizId || fetchedQuizId.current === quizId) return;
    fetchedQuizId.current = quizId;
    getQuiz(quizId).then(setQuiz);
  }, [session?.quizId]);

  // Late joiners read this off the broadcast — origin-derived so it follows
  // whatever domain the app is served from (live.aliensbrain.com once mapped).
  const joinHost = window.location.host;
  const joinUrl = `${window.location.origin}/?pin=${sessionCode}`;

  const index = session?.currentQuestionIndex ?? 0;
  const question = quiz?.questions[index] ?? null;
  const total = quiz?.questions.length ?? 0;
  const isStream = !!session?.youtubeUrl;
  const answersOpen = !!session?.answersOpen;
  const delayOptions = !!session?.delayOptions;
  const revealedIndex = session?.revealedAnswerIndex;
  const revealed = revealedIndex != null;
  // Read-the-question-first: keep the option tiles off the broadcast until the
  // host opens answers (they stay up after lock, for the reveal + distribution).
  const optionsHidden = delayOptions && !answersOpen && !revealed;
  const answeredCount = Object.values(answersByUser).filter(
    (byIdx) => byIdx?.[index] != null
  ).length;

  // ANSWER DISTRIBUTION at reveal — Kahoot's signature beat, and free here:
  // this host client already holds every answer (the scoring subscription), so
  // "62% picked ▲" costs zero extra bandwidth. Only shown AFTER lock & reveal —
  // a live distribution would leak the crowd's answer to still-answering phones
  // via the stream.
  const optionCount = question?.options.length ?? 0;
  const distribution: number[] = Array.from({ length: optionCount }, () => 0);
  for (const byIdx of Object.values(answersByUser)) {
    const chosen = byIdx?.[index];
    if (chosen != null && chosen >= 0 && chosen < optionCount) distribution[chosen]++;
  }

  // Flash the shout-out for a fixed window, aged on the server clock so a
  // late-joining or refreshed presenter tab doesn't replay an old one.
  const shoutoutAt = session?.shoutout?.at;
  useEffect(() => {
    if (!shoutoutAt) { setShoutoutVisible(false); return; }
    const remaining = SHOUTOUT_MS - (serverNow() - shoutoutAt);
    if (remaining <= 0) { setShoutoutVisible(false); return; }
    setShoutoutVisible(true);
    const id = setTimeout(() => setShoutoutVisible(false), remaining);
    return () => clearTimeout(id);
  }, [shoutoutAt]);

  // APP MODE (projector setup): show the same server-clock countdown the phones
  // run, so the room sees one consistent clock. Stream mode has no fair timer.
  useEffect(() => {
    // delayOptions: the clock starts when the host opens answers, not at advance.
    if (isStream || session?.status !== 'in_progress' || !question || !session.questionStartTime || (delayOptions && !answersOpen)) {
      setTimeLeft(null);
      return;
    }
    const startTime = session.questionStartTime;
    const timer = question.timer;
    const tick = () => setTimeLeft(Math.max(0, Math.ceil(timer - (serverNow() - startTime) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isStream, session?.status, session?.questionStartTime, question, delayOptions, answersOpen]);

  // Waiting room — giant join PIN + a scannable QR for the stream, plus live
  // arrivals: watching your own name pop onto the broadcast is the cheapest
  // fame moment we have.
  if (!session || session.status === 'not_started') {
    const recent = participants.slice(-24);
    return (
      <Shell>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-12 py-10">
          <p className="flex items-center gap-4 uppercase tracking-[0.3em] text-white/50 font-semibold">
            <span className="chip bg-ink-soft text-white/70 ring-1 ring-white/20">LOBBY</span>
            Scan the code or go to {joinHost}
          </p>
          <div className="mt-8 flex items-center justify-center gap-14">
            <div className="bg-white rounded-2xl p-4 pb-3">
              <JoinQr url={joinUrl} size={200} />
              <p className="text-ink font-bold mt-2 text-lg leading-none">Scan to join</p>
            </div>
            <div>
              <p className="text-2xl text-white/70">Game PIN</p>
              <div className="text-[11rem] leading-none font-display font-extrabold tracking-[0.1em] tabular-nums text-accent">{sessionCode}</div>
            </div>
          </div>
          {participants.length === 0 ? (
            <p className="mt-8 text-xl text-white/60">Waiting for players…</p>
          ) : (
            <>
              <p className="mt-8 text-2xl text-white/80">
                <span className="font-display font-extrabold text-white tabular-nums">{participants.length}</span> player{participants.length === 1 ? '' : 's'} in
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2 max-w-4xl">
                {recent.map((p) => (
                  <span key={p.uid} className="px-4 py-1.5 rounded-full bg-ink-soft ring-1 ring-white/15 text-lg font-semibold animate-pop">
                    {p.avatar ? `${p.avatar} ` : ''}{p.name}
                  </span>
                ))}
                {participants.length > recent.length && (
                  <span className="px-4 py-1.5 rounded-full text-lg text-white/50">
                    +{participants.length - recent.length} more
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </Shell>
    );
  }

  // Final standings — the fame moment.
  if (session.status === 'ended') {
    return (
      <Shell>
        <div className="flex-1 flex flex-col items-center justify-center px-12 py-10">
          <h1 className="text-6xl font-display font-extrabold tracking-tight mb-2">Final standings</h1>
          <p className="text-white/60 text-xl mb-10">Thanks for playing!</p>
          <ol className="w-full max-w-2xl space-y-3">
            {ranks.map((e, i) => (
              <li
                key={e.uid}
                className={`flex items-center gap-5 p-5 rounded-2xl animate-rise ${i < 3 ? 'bg-ink-soft ring-1 ring-accent/40' : 'bg-ink-soft/50'}`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <span className={rankChip(i)}>{i + 1}</span>
                <span className="flex-1 text-2xl font-bold truncate">{e.avatar ? `${e.avatar} ` : ''}{e.name}</span>
                <span className="text-2xl font-display font-extrabold text-accent tabular-nums">{e.score}</span>
              </li>
            ))}
          </ol>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {shoutoutVisible && session?.shoutout && <ShoutoutOverlay shoutout={session.shoutout} />}
      {/* top bar */}
      <div className="flex items-center justify-between px-12 py-6 text-lg">
        <span className="font-bold">Question {index + 1}{total ? ` / ${total}` : ''}</span>
        <span className="text-white/60">
          {joinHost} · PIN <span className="font-display font-extrabold tracking-widest tabular-nums text-white">{sessionCode}</span>
        </span>
        {!isStream && timeLeft !== null ? (
          <span className="flex items-center gap-3">
            <span className="text-white/80 font-bold tabular-nums">{answeredCount} answer{answeredCount === 1 ? '' : 's'} in</span>
            <span className={`grid place-items-center w-14 h-14 rounded-full text-2xl font-extrabold font-display ${timeLeft <= 5 && !revealed ? 'bg-tile-red text-white animate-pulse' : 'bg-ink-soft text-accent ring-2 ring-accent'}`}>
              {timeLeft}
            </span>
          </span>
        ) : answersOpen ? (
          <span className="flex items-center gap-3">
            <span className="text-white/80 font-bold tabular-nums">{answeredCount} answer{answeredCount === 1 ? '' : 's'} in</span>
            <span className="chip bg-tile-green text-white">ON AIR</span>
          </span>
        ) : revealed ? (
          <span className="chip bg-accent text-ink">REVEALED</span>
        ) : (
          <span className="chip bg-ink-soft text-white/70 ring-1 ring-white/20">LOCKED</span>
        )}
      </div>

      {/* question + options : leaderboard side panel */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-8 px-12 pb-14">
        <div className="lg:col-span-2 flex flex-col">
          <div className="flex-1 flex items-center justify-center text-center">
            <h1 className="text-5xl xl:text-6xl font-display font-extrabold tracking-tight leading-tight">{question?.questionText}</h1>
          </div>
          {question?.questionImage && (
            <img src={question.questionImage} alt="" className="max-h-64 mx-auto mb-6 rounded-2xl object-contain" />
          )}
          {toEmbedUrl(question?.questionVideo) && (
            <div className="relative w-full max-w-3xl mx-auto rounded-2xl overflow-hidden mb-6 bg-black" style={{ aspectRatio: '16 / 9' }}>
              <iframe
                src={toEmbedUrl(question?.questionVideo)!}
                title="Question video"
                className="absolute inset-0 w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {optionsHidden ? (
            // The read-aloud beat: shapes tease that options are coming, but no
            // text leaves the presenter's mouth-to-broadcast channel yet.
            <div className="rounded-2xl border-2 border-dashed border-white/20 p-10 text-center">
              <div className="flex items-center justify-center gap-5 text-4xl text-white/40">
                {question?.options.map((_, i) => (
                  <span key={i}>{shapeOf(i)}</span>
                ))}
              </div>
              <p className="mt-4 text-2xl text-white/60 font-semibold">
                Listen to the question — options are coming up
              </p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {question?.options.map((text, i) => {
              // Reveal: the correct tile pops for the camera, the rest fade —
              // the host narrates "the answer was ◆" straight off this screen.
              const state = revealed
                ? i === revealedIndex ? REVEAL_CORRECT : REVEAL_DIMMED
                : answersOpen ? '' : 'opacity-60';
              const pct = revealed && answeredCount > 0
                ? Math.round((distribution[i] / answeredCount) * 100)
                : null;
              return (
                <div
                  key={i}
                  className={`relative overflow-hidden flex items-center gap-4 p-6 rounded-2xl text-2xl font-display font-bold transition ${TILE_FRAME} ${tileClass(i)} ${state}`}
                >
                  {/* answer distribution — fills in behind the label at reveal */}
                  {pct !== null && (
                    <span
                      className="absolute inset-y-0 left-0 bg-black/25 transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  )}
                  <span className="relative text-4xl leading-none opacity-90">{shapeOf(i)}</span>
                  <span className="relative flex-1">{text}</span>
                  {pct !== null && (
                    <span className="relative text-2xl tabular-nums bg-black/30 rounded-lg px-3 py-1">{pct}%</span>
                  )}
                  {revealed && i === revealedIndex && <span className="relative text-3xl">✓</span>}
                </div>
              );
            })}
          </div>
          )}
        </div>

        {/* live leaderboard */}
        <div className="bg-ink-soft rounded-2xl ring-1 ring-ink-line p-6">
          <h2 className="text-2xl font-display font-extrabold mb-4">Leaderboard</h2>
          {ranks.length === 0 ? (
            <p className="text-white/50">Scores appear after the first reveal.</p>
          ) : (
            <ol className="space-y-2">
              {ranks.map((e, i) => (
                <li key={e.uid} className={`flex items-center gap-3 p-3 rounded-xl ${i < 3 ? 'bg-white/10 ring-1 ring-accent/40' : 'bg-white/5'}`}>
                  <span className={rankChip(i)}>{i + 1}</span>
                  <span className="flex-1 font-semibold truncate">{e.avatar ? `${e.avatar} ` : ''}{e.name}</span>
                  <span className="font-display font-extrabold text-accent tabular-nums">{e.score}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </Shell>
  );
};
